import { clearInMemoryUserCaches } from "./authSession";
import { removeManyFromLocalStorage, setManyToLocalStorage, setToLocalStorage } from "./localStorage";
import { clearAllOtpResendLocks } from "./forgotPasswordResendLock";
import { USER_ROLE_CAREGIVER, USER_ROLE_FAMILY, extractEmailFromToken, extractUserRoleFromToken, normalizeUserRoleFromClaim } from "./sessionContext";

export const NATIVE_CAREGIVER_ENTRY_PATHS = new Set(["/home", "/select-community"]);
export const NATIVE_FAMILY_ENTRY_PATHS = new Set(["/family-home", "/family-select-client"]);

/** @deprecated Use normalizeUserRoleFromClaim from sessionContext */
export const normalizeNativeUserRole = normalizeUserRoleFromClaim;

const RESIDENT_SESSION_KEYS = ["ResidentInfo", "ResidentName", "ResidentId", "CaregiverCheckinId", "CheckinTime", "shiftId"];

const CAREGIVER_SESSION_KEYS = ["caregiverId", "caregiverName", "caregiverEmailVerified", "caregiverMobileVerified", "communityId", "communityName", "communityFlag", ...RESIDENT_SESSION_KEYS];

const FAMILY_SESSION_KEYS = ["familyMemberName", "familyMemberId", "familyMemberClientId", "familyClientId", "familyClientName", "familyEmailVerified", "familyMobileVerified"];

const isValidParam = (value) => {
  const normalized = String(value ?? "").trim();
  return Boolean(normalized) && normalized !== "null" && normalized !== "undefined";
};

const resolveParamToken = (paramToken, isTokenExpired) => {
  if (!isValidParam(paramToken)) {
    return { token: undefined, hadInvalidToken: Boolean(paramToken) };
  }

  if (isTokenExpired(paramToken)) {
    return { token: null, hadInvalidToken: true };
  }

  return { token: paramToken, hadInvalidToken: false };
};

const resolveUserRole = (params, token, fallbackRole) => {
  const roleParam = params.get("role");
  if (isValidParam(roleParam)) {
    const fromParam = normalizeUserRoleFromClaim(roleParam);
    if (fromParam) {
      return fromParam;
    }
  }

  const fromToken = extractUserRoleFromToken(token);
  if (fromToken) {
    return fromToken;
  }

  return fallbackRole;
};

const resolveEmail = (params, token) => {
  const emailParam = params.get("email");
  if (isValidParam(emailParam)) {
    return emailParam.trim().toLowerCase();
  }

  return extractEmailFromToken(token);
};

const applyCaregiverNativeParams = (params, isTokenExpired) => {
  const caregiverId = params.get("caregiverId");
  const caregiverName = params.get("caregiverName");
  const paramToken = params.get("token");

  if (!caregiverId && !isValidParam(paramToken)) {
    return null;
  }

  removeManyFromLocalStorage([...FAMILY_SESSION_KEYS, ...RESIDENT_SESSION_KEYS]);
  clearInMemoryUserCaches();
  clearAllOtpResendLocks();

  const { token } = resolveParamToken(paramToken, isTokenExpired);

  if (caregiverId) {
    setToLocalStorage("caregiverId", caregiverId);
  }

  if (caregiverName) {
    setToLocalStorage("caregiverName", caregiverName);
  }

  if (token) {
    setToLocalStorage("user_token", token);
  }

  setToLocalStorage("userRole", resolveUserRole(params, token, USER_ROLE_CAREGIVER));

  const email = resolveEmail(params, token);
  if (email) {
    setToLocalStorage("userEmail", email);
  }

  return { token };
};

const applyFamilyNativeParams = (params, isTokenExpired) => {
  const familyMemberId = params.get("familyMemberId");
  const familyMemberName = params.get("familyMemberName");
  const paramToken = params.get("token");

  if (!familyMemberId && !isValidParam(paramToken)) {
    return null;
  }

  removeManyFromLocalStorage(CAREGIVER_SESSION_KEYS);
  clearInMemoryUserCaches();
  clearAllOtpResendLocks();

  const { token } = resolveParamToken(paramToken, isTokenExpired);

  const storage = {
    userRole: resolveUserRole(params, token, USER_ROLE_FAMILY),
  };

  if (familyMemberId) {
    storage.familyMemberId = familyMemberId;
  }

  if (familyMemberName) {
    storage.familyMemberName = familyMemberName;
  }

  const familyClientId = params.get("familyClientId") || params.get("clientId");
  const familyClientName = params.get("familyClientName") || params.get("clientName");

  if (isValidParam(familyClientId)) {
    storage.familyClientId = familyClientId;
    storage.familyMemberClientId = familyClientId;
  }

  if (isValidParam(familyClientName)) {
    storage.familyClientName = familyClientName;
  }

  if (token) {
    storage.user_token = token;
  }

  const email = resolveEmail(params, token);
  if (email) {
    storage.userEmail = email;
  }

  setManyToLocalStorage(storage);

  return { token };
};

/**
 * Seeds session localStorage from native WebView query params, then strips the query string.
 * Email and role fall back to JWT claims when not passed in the URL.
 * @returns {{ applied: boolean, token?: string|null }}
 */
export const applyNativeWebViewSearchParams = ({ pathname, rawSearch, isTokenExpired }) => {
  if (!rawSearch) {
    return { applied: false };
  }

  const params = new URLSearchParams(rawSearch);
  let result = null;

  if (NATIVE_CAREGIVER_ENTRY_PATHS.has(pathname)) {
    result = applyCaregiverNativeParams(params, isTokenExpired);
  } else if (NATIVE_FAMILY_ENTRY_PATHS.has(pathname)) {
    result = applyFamilyNativeParams(params, isTokenExpired);
  }

  if (!result) {
    return { applied: false };
  }

  window.history.replaceState({}, "", pathname);

  return {
    applied: true,
    ...(result.token !== undefined ? { token: result.token } : {}),
  };
};
